// V4-DL (spec-v4-downloads.md): pure helpers for the Download buttons and the project's "Download files" sheet.
import type { FileGroupId, ProjectFile, ProjectFiles } from "./types";

const GROUPS: { id: FileGroupId; label: string }[] = [
  { id: "video", label: "Finished video" },
  { id: "frames", label: "Frames" },
  { id: "clips", label: "Clips" },
  { id: "voice", label: "Voice" },
  { id: "sounds", label: "Sounds" },
  { id: "music", label: "Music" },
  { id: "characters", label: "Characters" },
];

/** "12 B", "820 KB", "4.2 MB", "1.3 GB" (1024-based, as phones show file sizes). Null/unknown: "". */
export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, "")} ${units[i]}`;
}

/** The file type tag shown on a row: "MP4", "PNG", "M4A" (from the file name the server picked). */
export function fileType(fileName: string): string {
  const m = /\.([a-z0-9]{1,5})$/i.exec(fileName);
  return m ? m[1]!.toUpperCase() : "";
}

/** "3 files, 4.2 MB" */
export function filesSummary(files: Pick<ProjectFile, "bytes">[]): string {
  const total = files.reduce((n, f) => n + (f.bytes ?? 0), 0);
  const size = fmtBytes(total);
  return `${files.length} file${files.length === 1 ? "" : "s"}${size && total ? `, ${size}` : ""}`;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : v === null || v === undefined ? "" : String(v));
const numOrNull = (v: unknown) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

/** Reads GET /api/projects/:id/files (or a flat list with `group` on each file) into groups in the fixed order,
 *  dropping files without a link and empty groups. */
export function normProjectFiles(v: unknown): ProjectFiles {
  const o = isObj(v) ? v : {};
  const flat: ProjectFile[] = [];
  const read = (x: unknown, group?: unknown) => {
    if (!isObj(x)) return;
    const g = str(x.group ?? group) as FileGroupId;
    const downloadUrl = str(x.downloadUrl ?? x.download_url);
    if (!downloadUrl || !GROUPS.some((k) => k.id === g)) return;
    flat.push({ id: str(x.id), label: str(x.label) || str(x.fileName), kind: str(x.kind), group: g, mime: x.mime ? str(x.mime) : null, bytes: numOrNull(x.bytes), fileName: str(x.fileName ?? x.file_name) || str(x.label), downloadUrl });
  };
  if (Array.isArray(o.groups)) for (const g of o.groups) if (isObj(g) && Array.isArray(g.files)) for (const f of g.files) read(f, g.id);
  if (Array.isArray(o.files)) for (const f of o.files) read(f);
  if (Array.isArray(v)) for (const f of v) read(f);
  const groups = GROUPS.map((g) => ({ ...g, files: flat.filter((f) => f.group === g.id) })).filter((g) => g.files.length);
  return { groups, count: flat.length, bytes: flat.reduce((n, f) => n + (f.bytes ?? 0), 0), packRenderId: o.packRenderId ? str(o.packRenderId) : null };
}

/** A link we can offer: the API's same-origin /api/media link (in mock mode a data:/blob: URL). Never a script URL. */
export function canDownload(url: string | null | undefined): url is string {
  if (typeof url !== "string" || !url.trim()) return false;
  return url.startsWith("/") ? !url.startsWith("//") : /^(https?:|data:|blob:)/i.test(url);
}
